import { Drawer, Space } from "antd";
import Button from "antd/es/button";
import Divider from "antd/es/divider";
import { ReactNode, useMemo } from "react";
import { FormattedMessage, IntlShape } from "react-intl";
import { useViewAddonsSidebars } from "./ViewAddons";
import { Sidebar, SidebarEnum } from "./ViewAddonsRenderer";
import useDevice from "../../../../hooks/useDevice";
import FAIcon from "../../FAIcon/FAIcon";
import CircleButton from "../../CircleButton/CircleButton";

interface ISidebar {
    content: ReactNode,
    title?: ReactNode,
    icon?: ReactNode,
    extra?: ReactNode;
}

const ViewAddonsSidebar = () => {
    const { isTablet } = useDevice();
    const { sidebars, sidebarType, setSidebarType } = useViewAddonsSidebars();

    const hasSidebar = useMemo(() => {
        return (sidebars?.length ?? 0) > 0;
    }, [sidebars?.length]);

    const sidebar = useMemo((): ISidebar => {
        const foundSidebar = sidebars?.find(s => s.type === sidebarType);

        if (foundSidebar && hasSidebar)
            return {
                content: foundSidebar.content,
                title: foundSidebar.titleBar?.title,
                icon: foundSidebar.titleBar?.icon,
                extra: foundSidebar.titleBar?.extra
            };

        return {
            content: <></>
        };
    }, [hasSidebar, sidebarType, sidebars]);

    const isVisible = useMemo(() => {
        return (sidebarType !== SidebarEnum.NONE || hasSidebar) && sidebars.map((s) => s.type).includes(sidebarType);
    }, [hasSidebar, sidebarType, sidebars]);

    return (
        <>
            {
                isTablet ?
                    <Drawer
                        closable={false}
                        className='__viewaddons-sidebar-drawer'
                        drawerStyle={{ backgroundColor: 'var(--background-color)' }}
                        onClose={() => setSidebarType(SidebarEnum.NONE)}
                        width={"100%"}
                        getContainer={false}
                        open={isVisible}
                        footer={[
                            <Space key={"container-tabs-drawer-footer"}>
                                <Button type='primary' onClick={() => setSidebarType(SidebarEnum.NONE)}>
                                    <FormattedMessage defaultMessage={'Show results'} />
                                </Button>
                            </Space>
                        ]}
                        footerStyle={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    >
                        <SidebarContent sidebar={sidebar} />
                    </Drawer>
                    :
                    <div className={`__viewaddons-sidebar ${isVisible ? '' : 'hide'}`}>
                        <SidebarContent sidebar={sidebar} />
                    </div>
            }
        </>
    );
};

export default ViewAddonsSidebar;

interface SidebarContentProps {
    sidebar: ISidebar;
}

const SidebarContent = (props: SidebarContentProps) => {
    return (
        <div className='__viewaddons-sidebar-container'>
            {
                props.sidebar.title ?
                    <>
                        <div className='__viewaddons-sidebar-title-container'>
                            <div className='__viewaddons-sidebar-icon'>
                                {props.sidebar.icon ?? null}
                                <div
                                    className='__viewaddons-sidebar-title'
                                >
                                    {props.sidebar.title}
                                </div>
                            </div>
                            <div className='__viewaddons-sidebar-extra'>
                                {props.sidebar.extra ?? null}
                            </div>
                        </div>
                        <Divider style={{ marginTop: '0px', marginBottom: '15px' }} />
                    </>
                    : null
            }
            <div className='__viewaddons-sidebar-content'>
                {props.sidebar.content}
            </div>
        </div>
    );
};

export const FilterSidebar = (reset: () => void, content: React.ReactNode, intl: IntlShape): Sidebar => ({
    type: SidebarEnum.FILTERS,
    button: {
        title: intl.formatMessage({ defaultMessage: 'Filters' }),
        icon: <FAIcon prefix={"fad"} name='filters' />
    },
    content: content,
    titleBar: {
        title: <FormattedMessage defaultMessage={'Filters'} />,
        icon: <FAIcon prefix={"fad"} name='filters' />,
        extra: <CircleButton
            className='__monthly-planning-btn-filter'
            type={"default"}
            icon={<FAIcon prefix="fad" name="rotate-right" color="dark" />}
            onClick={reset}
            title={intl.formatMessage({ defaultMessage: 'Reset' })}
        />
    }
});